<template>
	<div class="height-100 display-flex flex-column width-100">
		<v-container fluid>
			<v-row>
				<v-col cols="9">
					<EditableTextField
						ref="processTitleField"
						isEditOnClick
						:text="process?.title"
						placeholder="Enter Name"
						@onSave="updateTitileHandler"
						class="editable-text-field title pl-3"
					/>
				</v-col>
				<v-col cols="3">
					<v-btn :disabled="publishing" color="primary" @click="publishBtnHandler">Publish</v-btn>
				</v-col>
			</v-row>
		</v-container>

		<div class="flex-1 scroll-on-overflow">
			<ProcessNewNodeForm />
			<LoaderWrapper :isLoading="loading">
				<div class="process">
					<div id="process-graph-container" v-if="!loading">
						<cytoscape ref="cy" :config="config" :preConfig="preConfig" :afterCreated="afterCreated">
							<cy-element v-for="def in elements" :key="`${def.data.id}`" :definition="def" />
						</cytoscape>
					</div>
					<div class="horizontal-divider" />
				</div>
			</LoaderWrapper>
		</div>
		<v-container fluid>
			<v-row>
				<v-col cols="12">
					<v-btn color="error" class="delete-btn" @click="deleteBtnHandler">Delete</v-btn>
				</v-col>
			</v-row></v-container
		>
		<v-snackbar v-model="snackbar" :timeout="3000">{{ lastPublishResult }}</v-snackbar>
	</div>
</template>

<script>
import 'cytoscape-context-menus/cytoscape-context-menus.css';

import EditableTextField from '@/components/common/EditableTextField.vue';
import edgehandles from 'cytoscape-edgehandles';
import contextMenus from 'cytoscape-context-menus';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import { loading, id, process } from '@/store/modules/selectedProcess/getters';
import { load, publish, deleteNode, deleteProcess, updateProcess } from '@/store/modules/selectedProcess/actions';
import { createLink, deleteLink } from '@/store/modules/processes/actions';
import ProcessNewNodeForm from '@/components/Processes/ProcessNewNodeForm.vue';

const PROCESS_LINK_INVOKE_WHEN_DONE = 'invoke_when_done';

export default {
	name: 'Process',
	props: ['processId'],
	components: { ProcessNewNodeForm, EditableTextField },
	computed: {
		...mapGettersShorthand({ loading, id, process }),
		nodes() {
			return this.process?.nodes || [];
		},
		links() {
			return this.process?.links || [];
		},
		elements() {
			if (this.process) {
				const result = [
					...this.process.nodes.map(({ id, title }) => ({ data: { id: `node#${id}`, nodeId: id, title } })),
					...this.process.links.map(({ id, source, target, type, type_label: typeLabel }) => ({
						data: {
							id: `link#${type}#${source}-${target}`,
							linkId: id,
							source: `node#${source}`,
							target: `node#${target}`,
							type,
							typeLabel,
						},
					})),
				];
				return result;
			}
			return [];
		},
	},
	methods: {
		...mapActionsShorthand({ load, publish, createLink, deleteNode, deleteLink, deleteProcess, updateProcess }),
		updateTitileHandler(title) {
			if (title) {
				this.$refs.processTitleField.blur();
				this.updateProcess({ fields: { title } });
			}
		},
		preConfig(cytoscape) {
			if (typeof cytoscape('core', 'edgehandles') !== 'function') {
				cytoscape.use(edgehandles);
			}
			if (typeof cytoscape('core', 'contextMenus') !== 'function') {
				cytoscape.use(contextMenus);
			}
		},
		async afterCreated(cy) {
			await cy;
			if (cy) {
				this.cy = cy;

				// Context menu extension
				cy.contextMenus({
					menuItems: [
						{
							id: 'remove',
							content: 'remove',
							tooltipText: 'remove',
							selector: 'node, edge',
							onClickFunction: (event) => {
								const target = event.target || event.cyTarget;
								if (target.isNode()) {
									this.deleteNode(target.data('nodeId'));
								} else if (target.isEdge()) {
									this.deleteLink({ processId: this.id, linkId: target.data('linkId') });
								}
								target.remove();
							},
							hasTrailingDivider: true,
						},
					],
				});

				// Edges extension
				const eh = cy.edgehandles({
					canConnect: (sourceNode, targetNode) =>
						!this.linkExists({ sourceId: Number(sourceNode.data('nodeId')), targetId: Number(targetNode.data('nodeId')) }) &&
						!sourceNode.same(targetNode),
				});

				cy.on('ehcomplete', (event, sourceNode, targetNode, edge) => {
					edge.remove();
					this.createLink({
						processId: this.id,
						source: Number(sourceNode.data('nodeId')),
						target: Number(targetNode.data('nodeId')),
						type: PROCESS_LINK_INVOKE_WHEN_DONE,
					});
				});

				eh.enableDrawMode();

				// Reset layout on adding nodes
				cy.on('add', (event) => {
					// That's my way to drop unwanted events, like when you  drag a new edge before.
					if (event?.target?.data('nodeId')) {
						cy.layout({ name: 'circle', fit: true }).run();
					}
				});

				// Set layout
				cy.layout({ name: 'circle', fit: true, padding: 50 }).run();
			}
		},
		linkExists({ sourceId, targetId }) {
			return this.links.find(({ source: sId, target: tId }) => sourceId === sId && targetId === tId);
		},
		async publishBtnHandler() {
			this.publishing = true;
			this.lastPublishResult = '';
			try {
				await this.publish();
				this.lastPublishResult = 'Successfully published';
			} catch (e) {
				console.error(e);
				this.lastPublishResult = 'Error during publish';
			} finally {
				this.publishing = false;
				this.snackbar = true;
			}
		},
		async deleteBtnHandler() {
			this.deleteProcess();
		},
	},
	async mounted() {
		if (!this.process && this.processId) {
			await this.load({ id: this.processId });
		}
	},

	data: () => ({
		snackbar: false,
		lastPublishResult: '',
		publishing: false,
		config: {
			panningEnabled: false,

			style: [
				{
					selector: 'node',
					style: {
						'background-color': '#666',
						label: (e) => `${e.data('title')}`,
					},
				},
				{
					selector: 'edge',
					style: {
						width: 3,
						'line-color': '#ccc',
						'target-arrow-color': '#ccc',
						targetArrowShape: 'triangle',
						curveStyle: 'bezier',
						label: 'data(typeLabel)',
					},
				},
			],
		},
	}),
};
</script>
<style scoped>
.delete-btn {
	width: 100px;
}
</style>
